<template>
  <FlashNotifications ref="flash" />

  <!-- ── Image lightbox ── -->
  <Transition name="modal">
    <div v-if="lightbox.open"
      class="fixed inset-0 z-[60] flex items-center justify-center"
      @click.self="lightbox.open = false">
      <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" @click="lightbox.open = false"></div>
      <div class="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
        <img :src="lightbox.url" :alt="lightbox.alt"
          class="max-w-full max-h-[90vh] rounded-xl shadow-2xl object-contain"/>
        <button @click="lightbox.open = false"
          class="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
  </Transition>

  <!-- ── Action modal (approve / reject / schedule call) ── -->
  <Transition name="modal">
    <div v-if="actionModal.open"
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      @click.self="closeModal">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">

        <!-- Modal header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-xl flex items-center justify-center"
              :class="{
                'bg-emerald-50 dark:bg-emerald-900/20': actionModal.type === 'approve',
                'bg-red-50 dark:bg-red-900/20': actionModal.type === 'reject',
                'bg-blue-50 dark:bg-blue-900/20': actionModal.type === 'schedule',
              }">
              <svg v-if="actionModal.type === 'approve'" class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
              </svg>
              <svg v-else-if="actionModal.type === 'reject'" class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
              <svg v-else class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
              </svg>
            </div>
            <div>
              <h3 class="text-sm font-bold text-gray-900 dark:text-white leading-none">
                {{ actionModal.type === 'approve' ? 'Approve Verification' : actionModal.type === 'reject' ? 'Reject Verification' : 'Schedule Call' }}
              </h3>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ actionModal.verification?.user_full_name }}</p>
            </div>
          </div>
          <button @click="closeModal" class="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Modal body -->
        <div class="px-6 py-5 space-y-4">

          <!-- Approve: just confirm -->
          <template v-if="actionModal.type === 'approve'">
            <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              This will mark the user's account as verified and their badge will go live on their public profile.
              An email confirmation will be sent automatically.
            </p>
          </template>

          <!-- Reject: reason textarea -->
          <template v-else-if="actionModal.type === 'reject'">
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                Rejection reason <span class="text-gray-300 dark:text-gray-600 font-normal normal-case">(shown to user)</span>
              </label>
              <textarea v-model="actionModal.rejectionReason" rows="3" placeholder="e.g. Document images were unclear. Please resubmit with better quality photos."
                class="w-full px-3.5 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none transition-all">
              </textarea>
            </div>
            <p class="text-xs text-gray-400 dark:text-gray-500">The user will be able to resubmit after 7 days. An email with this reason will be sent automatically.</p>
          </template>

          <!-- Schedule call: booking link only -->
          <template v-else>
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Booking link</label>
              <input v-model="actionModal.callLink" type="url" placeholder="https://calendly.com/your-link"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"/>
            </div>
            <p class="text-xs text-gray-400 dark:text-gray-500">The user will receive an email with this link and can pick a time that suits them.</p>
          </template>

        </div>

        <!-- Modal footer -->
        <div class="px-6 pb-6 flex gap-3">
          <button @click="closeModal"
            class="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
            Cancel
          </button>
          <button @click="submitAction" :disabled="actionModal.loading || !canSubmitAction"
            class="flex-1 py-2.5 text-sm font-semibold rounded-xl text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="{
              'bg-emerald-600 hover:bg-emerald-700': actionModal.type === 'approve',
              'bg-red-500 hover:bg-red-600': actionModal.type === 'reject',
              'bg-blue-600 hover:bg-blue-700': actionModal.type === 'schedule',
            }">
            <span v-if="actionModal.loading" class="inline-flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              Processing...
            </span>
            <span v-else>
              {{ actionModal.type === 'approve' ? 'Approve' : actionModal.type === 'reject' ? 'Reject' : 'Schedule Call' }}
            </span>
          </button>
        </div>

      </div>
    </div>
  </Transition>

  <!-- ── Main page ── -->
  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117] flex flex-col">

    <!-- Sticky header -->
    <div class="sticky top-14 md:top-0 z-10 flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3 px-5 py-3.5 flex-wrap">

        <!-- Title + count -->
        <div class="flex-shrink-0">
          <div class="flex items-center gap-2">
            <h1 class="text-xl font-bold text-gray-900 dark:text-white">Verifications</h1>
            <span v-if="counts.all > 0"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 tabular-nums">
              {{ counts.all.toLocaleString() }}
            </span>
          </div>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Page {{ pagination.current_page }} of {{ pagination.total_pages || 1 }}
          </p>
        </div>

        <div class="flex-1"></div>

        <!-- Per page -->
        <div class="relative hidden sm:block">
          <select v-model="perPage" @change="resetAndFetch"
            class="pl-3 pr-8 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer appearance-none">
            <option v-for="n in [10, 20, 50, 100]" :key="n" :value="n">{{ n }} / page</option>
          </select>
          <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
          </svg>
        </div>

        <!-- Search -->
        <div class="relative w-48 sm:w-64">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" @input="onSearchInput" type="search" placeholder="Search by name or email..."
            class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"/>
        </div>
      </div>

      <!-- Status filter tabs -->
      <div class="flex gap-1 px-5 pb-3 overflow-x-auto no-scrollbar">
        <button v-for="tab in statusTabs" :key="tab.value"
          @click="setStatus(tab.value)"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
          :class="activeStatus === tab.value
            ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'">
          {{ tab.label }}
          <span v-if="counts[tab.countKey] > 0"
            class="px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums"
            :class="activeStatus === tab.value
              ? 'bg-white/20 dark:bg-black/20 text-inherit'
              : tab.badgeClass">
            {{ counts[tab.countKey] }}
          </span>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 px-5 py-5">

      <!-- Loading skeleton -->
      <template v-if="loading && verifications.length === 0">
        <div class="space-y-2.5 animate-pulse">
          <div v-for="i in 5" :key="i" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-4 flex items-center gap-4">
            <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
            <div class="flex-1 space-y-2">
              <div class="h-3.5 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-3 w-56 bg-gray-100 dark:bg-gray-800 rounded"></div>
            </div>
            <div class="hidden sm:flex items-center gap-2">
              <div class="h-6 w-20 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
              <div class="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Error -->
      <template v-else-if="loadError">
        <div class="flex flex-col items-center justify-center py-24 text-center">
          <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
            </svg>
          </div>
          <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">Failed to load</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">{{ loadError }}</p>
          <button @click="fetchVerifications" class="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg">Retry</button>
        </div>
      </template>

      <!-- Empty state -->
      <template v-else-if="verifications.length === 0">
        <div class="flex flex-col items-center justify-center py-24 text-center">
          <div class="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"/>
            </svg>
          </div>
          <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">No requests found</p>
          <p class="text-xs text-gray-400 dark:text-gray-500">
            {{ activeStatus ? 'No ' + activeStatus.replace('_', ' ') + ' requests' : 'No verification requests yet' }}
          </p>
        </div>
      </template>

      <!-- List -->
      <template v-else>
        <div class="space-y-2.5">
          <div v-for="v in verifications" :key="v.id"
            class="bg-white dark:bg-[#1C2128] rounded-xl border transition-colors overflow-hidden"
            :class="expandedRow === v.id
              ? 'border-blue-200 dark:border-blue-800/50'
              : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600'">

            <div class="flex items-center gap-4 px-5 py-4 cursor-pointer select-none"
              @click="toggleExpand(v.id)">

              <!-- Avatar -->
              <div class="flex-shrink-0">
                <img v-if="v.user_profile_picture_url" :src="v.user_profile_picture_url"
                  class="w-10 h-10 rounded-full object-cover" :alt="v.user_full_name"/>
                <div v-else
                  class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <span class="text-sm font-bold text-white leading-none">{{ v.user_avatar_name }}</span>
                </div>
              </div>

              <!-- User info -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ v.user_full_name }}</p>
                  <StatusBadge :status="v.status" />
                </div>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{{ v.user_email }}</p>
                <div class="flex items-center gap-3 mt-1.5 flex-wrap">
                  <!-- Document types -->
                  <div class="flex items-center gap-1">
                    <span v-for="dtype in v.document_types_summary" :key="dtype"
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      {{ docTypeIcon(dtype) }} {{ docTypeLabel(dtype) }}
                    </span>
                  </div>
                  <!-- Meta -->
                  <span class="text-[11px] text-gray-400 dark:text-gray-500">
                    {{ v.document_count }} file{{ v.document_count !== 1 ? 's' : '' }}
                  </span>
                  <span class="text-[11px] text-gray-400 dark:text-gray-500">
                    Submitted {{ formatDate(v.submitted_at) }}
                  </span>
                  <span v-if="v.reviewed_by_name" class="text-[11px] text-gray-400 dark:text-gray-500">
                    Reviewed by {{ v.reviewed_by_name }}
                  </span>
                </div>
                <!-- Rejection reason -->
                <p v-if="v.status === 'rejected' && v.rejection_reason"
                  class="mt-1.5 text-xs text-red-500 dark:text-red-400 truncate max-w-md">
                  "{{ v.rejection_reason }}"
                </p>
                <!-- Call scheduled -->
                <span v-if="v.status === 'call_scheduled'"
                  class="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                  </svg>
                  Booking link sent
                </span>
              </div>

              <!-- Actions -->
              <div class="flex items-center gap-2 flex-shrink-0" @click.stop>

                <!-- View profile link -->
                <a v-if="v.user_url" :href="'/' + v.user_url" target="_blank"
                  class="hidden sm:flex w-8 h-8 rounded-lg items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="View public profile">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                  </svg>
                </a>

                <!-- Pending actions -->
                <template v-if="v.status === 'pending_review'">
                  <button @click="openModal('schedule', v)"
                    class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-blue-200 dark:border-blue-800/50 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                    </svg>
                    Schedule call
                  </button>
                  <button @click="openModal('approve', v)"
                    class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                    </svg>
                    Approve
                  </button>
                  <button @click="openModal('reject', v)"
                    class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 dark:border-red-800/50 text-red-500 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                    </svg>
                    Reject
                  </button>
                </template>

                <!-- Call scheduled: approve or reject -->
                <template v-else-if="v.status === 'call_scheduled'">
                  <a v-if="v.call_link" :href="v.call_link" target="_blank"
                    class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                    </svg>
                    Booking link
                  </a>
                  <button @click="openModal('approve', v)"
                    class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors">
                    Approve
                  </button>
                  <button @click="openModal('reject', v)"
                    class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 dark:border-red-800/50 text-red-500 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 transition-colors">
                    Reject
                  </button>
                </template>

                <!-- Mobile kebab -->
                <div class="relative sm:hidden">
                  <button @click="toggleMobileMenu(v.id)"
                    class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                    </svg>
                  </button>
                  <div v-if="mobileMenuOpen === v.id"
                    class="absolute right-0 top-9 z-20 w-44 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 shadow-lg overflow-hidden">
                    <template v-if="v.status === 'pending_review' || v.status === 'call_scheduled'">
                      <button v-if="v.status === 'pending_review'" @click="openModal('schedule', v); mobileMenuOpen = null" class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800">Schedule call</button>
                      <a v-if="v.status === 'call_scheduled' && v.call_link" :href="v.call_link" target="_blank" class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800">Booking link</a>
                      <button @click="openModal('approve', v); mobileMenuOpen = null" class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-emerald-600 hover:bg-gray-50 dark:hover:bg-gray-800">Approve</button>
                      <button @click="openModal('reject', v); mobileMenuOpen = null" class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-gray-50 dark:hover:bg-gray-800">Reject</button>
                    </template>
                    <a v-if="v.user_url" :href="'/' + v.user_url" target="_blank" class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">View profile</a>
                  </div>
                </div>

                <!-- Expand chevron -->
                <div class="w-6 h-6 flex items-center justify-center text-gray-300 dark:text-gray-600 transition-transform duration-200 flex-shrink-0"
                  :class="expandedRow === v.id ? 'rotate-180' : ''"
                  @click="toggleExpand(v.id)">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
                  </svg>
                </div>

              </div>
            </div>

            <!-- Expandable document panel -->
            <Transition
              enter-active-class="transition-all duration-200 ease-out overflow-hidden"
              enter-from-class="opacity-0 max-h-0"
              enter-to-class="opacity-100 max-h-[700px]"
              leave-active-class="transition-all duration-150 ease-in overflow-hidden"
              leave-from-class="opacity-100 max-h-[700px]"
              leave-to-class="opacity-0 max-h-0">
              <div v-if="expandedRow === v.id"
                class="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-[#161b22] px-5 py-4">
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                  Uploaded Documents
                </p>
                <template v-if="v.verification_documents?.length">
                  <div v-for="dtype in docTypesInVerification(v)" :key="dtype" class="mb-4 last:mb-0">
                    <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                      <span>{{ docTypeIcon(dtype) }}</span> {{ docTypeLabel(dtype) }}
                    </p>
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      <button v-for="doc in docsOfType(v, dtype)" :key="doc.id"
                        @click="doc.file_url && openLightbox(doc)"
                        :class="doc.file_url ? 'cursor-zoom-in' : 'cursor-default'"
                        class="group relative rounded-xl overflow-hidden bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors block w-full"
                        style="aspect-ratio:4/3">
                        <img v-if="doc.file_url" :src="doc.file_url"
                          class="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                          :alt="docTypeLabel(doc.document_type) + ' ' + doc.side"/>
                        <div v-else class="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-300 dark:text-gray-600">
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/>
                          </svg>
                          <span class="text-[9px]">No file</span>
                        </div>
                        <!-- Side badge -->
                        <span class="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded-md bg-black/50 text-[9px] font-bold text-white uppercase tracking-wide">
                          {{ doc.side }}
                        </span>
                        <!-- Zoom hint icon -->
                        <span v-if="doc.file_url" class="absolute top-1.5 right-1.5 w-5 h-5 rounded bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6"/>
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>
                </template>
                <p v-else class="text-xs text-gray-400 dark:text-gray-500 italic">No documents attached.</p>
              </div>
            </Transition>

          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total_pages > 1" class="flex items-center justify-between mt-5">
          <button @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page <= 1"
            class="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
            </svg>
            Previous
          </button>
          <span class="text-sm text-gray-500 dark:text-gray-400">
            Page {{ pagination.current_page }} of {{ pagination.total_pages }}
          </span>
          <button @click="changePage(pagination.current_page + 1)" :disabled="pagination.current_page >= pagination.total_pages"
            class="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Next
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m9 5 7 7-7 7"/>
            </svg>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import axios from 'redaxios'
import FlashNotifications from 'controllers/vue_components/flash_toast'

// ── Sub-components ────────────────────────────────────────────────────────────

const StatusBadge = {
  props: ['status'],
  template: `
    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap" :class="classes[status] || classes.default">
      {{ labels[status] || status }}
    </span>
  `,
  setup() {
    return {
      labels: {
        pending_review: 'Pending Review',
        call_scheduled: 'Call Scheduled',
        approved:       'Approved',
        rejected:       'Rejected',
      },
      classes: {
        pending_review: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
        call_scheduled: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
        approved:       'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
        rejected:       'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400',
        default:        'bg-gray-100 dark:bg-gray-800 text-gray-500',
      },
    }
  },
}

// ── State ────────────────────────────────────────────────────────────────────

const flash          = ref(null)
const loading        = ref(false)
const loadError      = ref(null)
const verifications  = ref([])
const pagination     = ref({ current_page: 1, total_pages: 1, total_records: 0, per_page: 20 })
const counts         = ref({ all: 0, pending_review: 0, call_scheduled: 0, approved: 0, rejected: 0 })
const search         = ref('')
const activeStatus   = ref('')
const perPage        = ref(20)
const mobileMenuOpen = ref(null)
const expandedRow    = ref(null)
let searchTimer      = null

const lightbox = ref({ open: false, url: '', alt: '' })

const actionModal = ref({
  open:             false,
  type:             null,
  verification:     null,
  rejectionReason:  '',
  callLink:         '',
  loading:          false,
})

const statusTabs = [
  { value: '',               label: 'All',            countKey: 'all',            badgeClass: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400' },
  { value: 'pending_review', label: 'Pending Review', countKey: 'pending_review', badgeClass: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
  { value: 'call_scheduled', label: 'Call Scheduled', countKey: 'call_scheduled', badgeClass: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
  { value: 'approved',       label: 'Approved',       countKey: 'approved',       badgeClass: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' },
  { value: 'rejected',       label: 'Rejected',       countKey: 'rejected',       badgeClass: 'bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400' },
]

const DOC_TYPES = {
  id_card:         { label: 'ID Card',        icon: '🪪' },
  resident_permit: { label: 'Resident Permit', icon: '📄' },
  passport:        { label: 'Passport',        icon: '📕' },
  driving_license: { label: 'Driving License', icon: '🚗' },
}

// ── Computed ─────────────────────────────────────────────────────────────────

const canSubmitAction = computed(() => {
  if (actionModal.value.type === 'schedule') {
    return actionModal.value.callLink.trim().length > 0
  }
  return true
})

// ── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  fetchVerifications()
  document.addEventListener('click', handleOutsideClick)
  document.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('keydown', handleKeyDown)
})

// ── Methods ──────────────────────────────────────────────────────────────────

function openLightbox(doc) {
  lightbox.value = { open: true, url: doc.file_url, alt: docTypeLabel(doc.document_type) + ' ' + doc.side }
}

function handleKeyDown(e) {
  if (e.key === 'Escape') {
    if (lightbox.value.open) { lightbox.value.open = false; return }
    if (actionModal.value.open) closeModal()
  }
}

function csrfToken() {
  return document.querySelector('[name="csrf-token"]')?.getAttribute('content') ?? ''
}

async function fetchVerifications() {
  loading.value = true
  loadError.value = null
  try {
    const params = new URLSearchParams({
      page:     pagination.value.current_page,
      per_page: perPage.value,
    })
    if (search.value.trim())  params.set('search', search.value.trim())
    if (activeStatus.value)   params.set('status', activeStatus.value)

    const res = await axios.get(`/api/v1/admin/verifications?${params}`, {
      headers: { 'X-CSRF-TOKEN': csrfToken() },
    })
    if (res.data.success) {
      verifications.value = res.data.data.verifications
      pagination.value    = res.data.data.pagination
      counts.value        = res.data.data.counts
    } else {
      loadError.value = res.data.error || 'Failed to load.'
    }
  } catch {
    loadError.value = 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}

function resetAndFetch() {
  pagination.value.current_page = 1
  fetchVerifications()
}

function setStatus(status) {
  activeStatus.value = status
  resetAndFetch()
}

function changePage(page) {
  pagination.value.current_page = page
  fetchVerifications()
}

function onSearchInput() {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(resetAndFetch, 350)
}

function toggleMobileMenu(id) {
  mobileMenuOpen.value = mobileMenuOpen.value === id ? null : id
}

function toggleExpand(id) {
  expandedRow.value = expandedRow.value === id ? null : id
}

function docTypesInVerification(v) {
  return [...new Set((v.verification_documents || []).map(d => d.document_type))]
}

function docsOfType(v, dtype) {
  return (v.verification_documents || []).filter(d => d.document_type === dtype)
}

function handleOutsideClick(e) {
  if (!e.target.closest('[data-mobile-menu]')) mobileMenuOpen.value = null
}

function docTypeLabel(type) { return DOC_TYPES[type]?.label ?? type }
function docTypeIcon(type)  { return DOC_TYPES[type]?.icon  ?? '📄' }

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatDateTime(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ── Modal ────────────────────────────────────────────────────────────────────

function openModal(type, verification) {
  actionModal.value = {
    open:            true,
    type,
    verification,
    rejectionReason: '',
    callLink:        verification.call_link ?? '',
    loading:         false,
  }
}

function closeModal() {
  if (actionModal.value.loading) return
  actionModal.value.open = false
}

async function submitAction() {
  if (actionModal.value.loading || !canSubmitAction.value) return
  actionModal.value.loading = true

  const { type, verification } = actionModal.value
  const headers = { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() }

  try {
    let res
    if (type === 'approve') {
      res = await axios.put(`/api/v1/admin/verifications/${verification.id}/approve`, {}, { headers })
    } else if (type === 'reject') {
      res = await axios.put(`/api/v1/admin/verifications/${verification.id}/reject`,
        { rejection_reason: actionModal.value.rejectionReason }, { headers })
    } else {
      res = await axios.put(`/api/v1/admin/verifications/${verification.id}/schedule_call`, {
        call_link: actionModal.value.callLink,
      }, { headers })
    }

    if (res.data.success) {
      const updated = res.data.data
      const idx = verifications.value.findIndex(v => v.id === updated.id)
      if (idx !== -1) verifications.value.splice(idx, 1, updated)
      counts.value.pending_review = Math.max(0, counts.value.pending_review - 1)
      if (type === 'approve') counts.value.approved++
      else if (type === 'reject') counts.value.rejected++
      else counts.value.call_scheduled++
      flash.value?.show(res.data.message, 'notice')
      actionModal.value.open = false
    } else {
      flash.value?.show(res.data.error || 'Action failed.', 'alert')
    }
  } catch {
    flash.value?.show('Something went wrong. Please try again.', 'alert')
  } finally {
    actionModal.value.loading = false
  }
}
</script>

<style scoped>
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
